import { useEffect, useMemo, useRef, useState } from 'react'

import { Form, FormRef } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { HelperText } from '@tribeplatform/react-ui-kit/HelperText'

import { DefaultHomepageFormField } from './FormFields/DefaultHomepageFormField.js'
import { NewUsersHomepageFormField } from './FormFields/NewUsersHomepageFormField.js'
import { ReturningUsersHomepageFormField } from './FormFields/ReturningUsersHomepageFormField.js'
import { HomepageSettingsFormState } from './types.js'
import { useHomepageSettings } from './useHomepageSettings.js'

export const HomepageSettings = () => {
  const { $t } = useI18n()

  const formRef = useRef<FormRef>()

  const {
    defaultHomepage,
    newUsersHomepage,
    returningUsersHomepage,
    isLoading,
    updateSettings,
    isUpdating,
    errors,
  } = useHomepageSettings()

  const defaultFormValues: HomepageSettingsFormState = useMemo(
    () => ({
      defaultHomepage: defaultHomepage?.space ?? null,
      newUsersHomepage: newUsersHomepage?.space ?? null,
      returningUsersHomepage: returningUsersHomepage?.space ?? null,
    }),
    [
      defaultHomepage?.space,
      newUsersHomepage?.space,
      returningUsersHomepage?.space,
    ],
  )

  useEffect(() => {
    if (formRef.current?.methods) {
      formRef.current.methods.reset(defaultFormValues)
    }
  }, [defaultFormValues])

  const [defaultHomeIsPrivate, setDefaultHomeIsPrivate] =
    useState<boolean>(false)
  return (
    <Card id="homepage-settings">
      <Card.Header
        title={$t({
          defaultMessage: 'Homepage settings',
          id: 'Admin.HomepageSettings',
        })}
        description={
          <T
            defaultMessage="Choose the landing page for visitors and members when they visit the community."
            id="Admin.HomepageSettings.HelperText"
          />
        }
      />
      <Card.Content className="flex flex-col space-y-5">
        <Form
          defaultValues={defaultFormValues}
          ref={formRef}
          onSubmit={updateSettings}
        >
          {({ formState: { isDirty } }) => (
            <Form.Layout>
              <DefaultHomepageFormField
                formKey="defaultHomepage"
                defaultValue={defaultFormValues.defaultHomepage}
                isLoading={isLoading}
                onChange={space => {
                  setDefaultHomeIsPrivate(space.private)
                }}
              />
              <ReturningUsersHomepageFormField
                formKey="returningUsersHomepage"
                defaultValue={defaultFormValues.returningUsersHomepage}
                isLoading={isLoading}
                defaultHomepage={defaultHomepage?.space}
              />
              <NewUsersHomepageFormField
                formKey="newUsersHomepage"
                defaultValue={defaultFormValues.newUsersHomepage}
                isLoading={isLoading}
                defaultHomepage={defaultHomepage?.space}
              />
              {errors.length > 0 && (
                <HelperText status="error" highlighted className="mt-2">
                  <T
                    defaultMessage="Something went wrong and we were unable to update homepage settings. Please try again."
                    id="Admin.HomepageSettings.UpdateError"
                  />
                </HelperText>
              )}
              <Form.Actions>
                <SubmitButton
                  disabled={defaultHomeIsPrivate || !isDirty}
                  isLoading={isUpdating || isLoading}
                />
              </Form.Actions>
            </Form.Layout>
          )}
        </Form>
      </Card.Content>
    </Card>
  )
}

const SubmitButton = ({
  disabled,
  isLoading,
}: {
  disabled: boolean
  isLoading: boolean
}) => {
  return (
    <Button
      type="submit"
      variant="primary"
      disabled={disabled}
      loading={isLoading}
    >
      <T defaultMessage="Update" id="Generics.Update" />
    </Button>
  )
}
